import {FC, MouseEvent, useState} from 'react';
import {Link} from 'react-router-dom';
import {ApartmentOutlined} from '@mui/icons-material';
import {Box, Popover, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetIcons} from 'src/components/mobile/ActionSheet';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {BusinessDTO} from 'src/types';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {MenuButton} from 'src/components/tables/respnsiveTable/MenuButton';
import {BusinessRowActions} from '../BusinessRowActions';
import {activePillSx, popoverSx, titleLinkSx} from './BusinessItemCard.sx';

type BusinessItemCardProps = {
    business: BusinessDTO;
    activeId: number;
};

/**
 * The card form of a business row.
 *
 * It carries the columns the grid carries and, behind the menu, the very actions component the row
 * uses - so making a business active or editing it does the same two things from a phone as from a
 * desktop, rather than a second implementation that can drift from it.
 */
export const BusinessItemCard: FC<BusinessItemCardProps> = ({business, activeId}) => {
    const intl = useIntl();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Written exactly as the address column writes it, down to the empty case.
    const address = [
        business.address?.street,
        business.address?.houseNumber,
        business.address?.zipCode,
        business.address?.city,
    ]
        .filter(Boolean)
        .join(' ') || '-';

    // The menu the card's head carries. The sheet and the popover are portalled, so sitting in the
    // head's row costs the layout nothing.
    const menuAction = (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<ApartmentOutlined/>}
                    title={business.name}
                    subtitle={address}
                >
                    <ActionSheetIcons>
                        <BusinessRowActions businessId={business.id}/>
                    </ActionSheetIcons>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <BusinessRowActions businessId={business.id}/>
                </Popover>
            )}
        </>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<ApartmentOutlined/>}
                title={
                    /* Where opening the row goes on a desktop. */
                    <Link to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.businesses, String(business.id)])}>
                        <Typography
                            component="span"
                            sx={titleLinkSx}
                        >
                            {business.name}
                        </Typography>
                    </Link>
                }
                subtitle={address}
                action={menuAction}
            />
            {business.id === activeId && (
                <Box sx={phonePillsRowSx}>
                    <Typography
                        component="span"
                        sx={activePillSx}
                    >
                        {intl.formatMessage({id: 'active'})}
                    </Typography>
                </Box>
            )}
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'uuid'}),
                        value: business.uuid ?? '-',
                    },
                    {
                        label: intl.formatMessage({id: 'backendPricePerUser'}),
                        value: `${business.userPerMonthCost ?? 0} ${business.userPerMonthCurrency ?? ''}`.trim(),
                    },
                    {
                        label: intl.formatMessage({id: 'rfidCardPrice'}),
                        // Stored in cents, shown the way the column shows it.
                        value: `${(business.rfidCost ?? 0) / 100} ${business.rfidCostCurrency ?? ''}`.trim(),
                    },
                ]}
            />
        </TableCard>
    );
};
